import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render-time exceptions in its subtree and shows a recoverable fallback instead of
 * letting one thrown error blank the entire app. Wrap page content (the router Outlet) so a
 * crash in one page keeps the surrounding nav/header intact.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the failure so it isn't silently swallowed into a blank screen.
    console.error("Unhandled error rendering page:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, textAlign: "center", color: "#5b6b7b" }}>
          <h2 style={{ marginBottom: 8 }}>Something went wrong.</h2>
          <p>Try refreshing the page. If it keeps happening, the data may be unavailable.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
